/** @jsxImportSource theme-ui @jsxRuntime classic */
import { Box, Button, Flex, Link } from '@tishman/components';
import { H } from '@hzdg/sectioning';
import React, { useContext } from 'react';
import { ThemeUIStyleObject } from 'theme-ui';

import { useExperiment } from '../../services/firebase/useExperiment';
import { logExperimentClick } from '../../services/firebase/analytics';
import { Flow } from '../../services/viva/constants';
import { selectFlow } from '../../store/selectors';
import { useAppSelector } from '../../store';

import { CheckoutCountdown } from './CheckoutCountdown';

import { TimeContext, useBuyTicketsModal } from '.';

export interface HeroLink {
  label?: string;
  modal?: string;
  url?: string;
}

export interface HeroProps {
  children?: React.ReactNode;
  /** Title of the ticket flow */
  title: React.ReactNode;
  /** Description of the ticket flow */
  description?: React.ReactNode;
  /** Optional link for up-sells */
  link?: HeroLink;
  /** Optional title css white-space */
  titleWhiteSpace?: string;
  fullWidth?: boolean;
}

export const Hero = ({
  children,
  title,
  description,
  link,
  titleWhiteSpace = 'normal',
  fullWidth = false,
}: HeroProps): JSX.Element => {
  const { setValue: setModal } = useBuyTicketsModal();
  const timeValue = useContext(TimeContext);
  const flow = useAppSelector(selectFlow);
  const { variant, loading } = useExperiment('buy_tickets_hero_link_variant');

  const LinkButton = ({ link }: { link: HeroLink }): React.JSX.Element => {
    if (link) {
      // Common button styles
      const buttonStyles = {
        p: 0,
        mt: 24,
        textAlign: 'left',
        maxWidth: [257, '100%'],
      } as ThemeUIStyleObject;

      // Common link styles
      const linkStyles = {
        'display': 'inline-block',
        'pb': 2,
        'textDecoration': 'none',
        'color': 'inherit',
        ':hover': { textDecoration: 'none' },
      } as ThemeUIStyleObject;

      if (link.url) {
        // Special case for TOR_GA flow with A/B testing
        if (flow === Flow.TOR_GA || flow === Flow.NONE) {
          // Handle loading state or variant B
          const isExpressPass = variant === 'B';
          const showExpressPass = !loading && isExpressPass;
          const linkText = showExpressPass ? 'Express Pass' : link.label;
          const linkUrl = showExpressPass
            ? '/buy-tickets/express-pass/'
            : link.url;

          const handleClick = () => {
            logExperimentClick('buy_tickets_hero_link_variant', variant);
          };

          return (
            <Button
              aria-label={loading ? 'Loading...' : linkText}
              sx={
                {
                  ...buttonStyles,
                  visibility: loading ? 'hidden' : 'visible',
                } as ThemeUIStyleObject
              }
              variant="underline"
            >
              {showExpressPass ? (
                <Link href={linkUrl} onClick={handleClick} sx={linkStyles}>
                  {linkText}
                </Link>
              ) : (
                <Link
                  onClick={handleClick}
                  sx={linkStyles}
                  target="_blank"
                  to={linkUrl}
                >
                  {linkText}
                </Link>
              )}
            </Button>
          );
        }

        // Default case for URL links
        return (
          <Box>
            {link.url ? (
              <Button
                aria-label={link.label}
                sx={buttonStyles}
                variant="underline"
              >
                <Link sx={linkStyles} target="_blank" to={link.url}>
                  {link.label}
                </Link>
              </Button>
            ) : (
              <Button
                aria-label={link.label}
                onClick={() => {
                  setModal(new Error(link.modal));
                }}
                sx={{ ...buttonStyles, pt: 0 }}
                variant="underline"
              >
                {link.label}
              </Button>
            )}
          </Box>
        );
      }

      // Case for modal links
      return (
        <Button
          aria-label={link.label}
          onClick={() => {
            setModal(new Error(link.modal));
          }}
          sx={
            {
              ...buttonStyles,
              pb: 2,
              pt: 0,
            } as ThemeUIStyleObject
          }
          variant="underline"
        >
          {link.label}
        </Button>
      );
    }

    // Default return when link is falsy
    return <React.Fragment></React.Fragment>;
  };

  return (
    <Box
      sx={{
        pb: description ? 5 : 0,
        mb: description ? 2 : 0,
      }}
    >
      <Flex
        sx={{
          alignItems: 'flex-start',
          mt: 2,
          pb: description ? 1 : 5,
          mb: 2,
          justifyContent: 'space-between',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'flex-start',
            rowGap: 3,
            flex: [1, 'initial'],
          }}
        >
          <H
            sx={{
              variant: 'text.ticketsHeading',
              maxWidth: [fullWidth ? '100%' : 220, '100%'],
              fontSize: ['32px', 7],
              whiteSpace: titleWhiteSpace,
            }}
          >
            {title}
          </H>
          <CheckoutCountdown />
          {link && <LinkButton link={link} />}
        </Flex>
        <Flex
          // putting the value above in sr-only(screen reader only)
          aria-hidden={true}
          sx={{
            textAlign: 'right',
            alignItems: 'center',
            justifyContent: timeValue ? 'flex-start' : 'flex-end',
          }}
        >
          {children}
        </Flex>
      </Flex>
      {!!description && (
        <Box sx={{ maxWidth: ['100%', 635] }}>{description}</Box>
      )}
    </Box>
  );
};
